import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import useSearchMessages from "../../hooks/useSearchMessages";
import useDocumentScrollPagination from "../../hooks/useDocumentScrollPagination";
import useScrollPreload from "../../hooks/useScrollPreload";
import { i18n } from "../../translate/i18n";
import { buildMatchSnippet, groupMessagesByMonth, normalizeSearchTerm } from "../../utils/searchUtils";
import ContactAvatar from "../ContactAvatar";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import DescriptionIcon from "@material-ui/icons/Description";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import VideocamIcon from "@material-ui/icons/Videocam";
import LinkIcon from "@material-ui/icons/Link";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { normalizeFileName, getExtensionFromName } from "../../utils/fileName";

const MESSAGE_SIGNATURE_PREFIX_REGEX = /^\*[^\n*]{1,60}:\*\s*\n?/;
const BACKEND_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/g, "");

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "avi", "mkv", "webm", "m4v"]);
const DOCUMENT_EXTENSIONS = new Set(["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "odt", "rtf"]);

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  listContainer: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "auto",
    ...theme.neoUI.scrollbar.default,
  },
  sectionHeader: {
    padding: theme.spacing(2, 2, 1),
    color: theme.palette.text.secondary,
    fontWeight: 700,
    textTransform: "none",
  },
  monthHeader: {
    padding: theme.spacing(2, 2, 1),
    color: theme.palette.text.secondary,
    fontWeight: 700,
  },
  empty: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  mediaItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  mediaThumb: {
    width: 56,
    height: 56,
    borderRadius: 8,
    objectFit: "cover",
    backgroundColor: theme.palette.action.hover,
    flex: "none",
  },
  mediaRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: "none",
    marginLeft: theme.spacing(1),
  },
  mediaMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
}));

const normalizeStoredMediaPath = (mediaUrl = "") => {
  const value = String(mediaUrl || "").trim();
  if (!value) {
    return "";
  }

  if (/^(https?:|blob:|data:)/i.test(value)) {
    return value;
  }

  const normalizedSlashes = value.replace(/\\/g, "/").replace(/^\/+/, "");
  const lower = normalizedSlashes.toLowerCase();

  if (
    lower.startsWith("public/") ||
    lower.startsWith("media/") ||
    lower.startsWith("location-thumbnails/") ||
    lower.startsWith("profilepicture/")
  ) {
    return `/${normalizedSlashes}`;
  }

  return `/public/${normalizedSlashes}`;
};

const resolveAbsoluteMediaUrl = (mediaUrl = "") => {
  const normalizedPath = normalizeStoredMediaPath(mediaUrl);
  if (!normalizedPath) {
    return "";
  }

  if (/^(https?:|blob:|data:)/i.test(normalizedPath)) {
    return normalizedPath;
  }

  const baseUrl = BACKEND_BASE_URL || window.location.origin;
  return `${baseUrl}${encodeURI(normalizedPath)}`;
};

const resolveThumbUrl = (mediaUrl = "", thumbsFolder = "") => {
  if (!mediaUrl || !thumbsFolder) {
    return "";
  }

  const absoluteMediaUrl = resolveAbsoluteMediaUrl(mediaUrl);
  if (!absoluteMediaUrl) {
    return "";
  }

  try {
    const urlObj = new URL(absoluteMediaUrl);
    const pathname = decodeURIComponent(urlObj.pathname || "");
    const fileNameWithExtension = pathname.split("/").pop() || "";
    const fileNameWithoutExtension = fileNameWithExtension.replace(/\.[^.]+$/i, "");

    if (!fileNameWithoutExtension) {
      return "";
    }

    return `${urlObj.origin}/public/${thumbsFolder}/${encodeURIComponent(fileNameWithoutExtension)}_thumb.jpg`;
  } catch (error) {
    return "";
  }
};

const resolveMediaCategory = (message, mediaFilter) => {
  const mediaType = String(message?.mediaType || "").toLowerCase();

  if (mediaType === "image" || mediaType.startsWith("image/")) {
    return "image";
  }
  if (mediaType === "video" || mediaType.startsWith("video/")) {
    return "video";
  }
  if (
    mediaType === "document" ||
    mediaType === "application" ||
    mediaType.startsWith("application/")
  ) {
    return "document";
  }

  const extension = getExtensionFromName(normalizeFileName("", message?.mediaUrl, ""));
  if (IMAGE_EXTENSIONS.has(extension)) {
    return "image";
  }
  if (VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }
  if (DOCUMENT_EXTENSIONS.has(extension)) {
    return "document";
  }

  const normalizedFilter = String(mediaFilter || "").toLowerCase();
  if (normalizedFilter === "photos") return "image";
  if (normalizedFilter === "videos") return "video";
  if (normalizedFilter === "documents") return "document";
  return "other";
};

const resolveMediaLabel = (message, mediaFilter) => {
  const mediaCategory = resolveMediaCategory(message, mediaFilter);
  const normalizedFileName = normalizeFileName(
    "",
    message?.mediaUrl,
    "documento.pdf"
  );

  if (mediaCategory === "image") return "📷 Foto";
  if (mediaCategory === "video") return "🎥 Vídeo";
  if (mediaCategory === "document") return normalizedFileName;

  return message?.body || "";
};

const resolveMediaIcon = (message, mediaFilter) => {
  const normalizedFilter = String(mediaFilter || "").toLowerCase();
  const mediaCategory = resolveMediaCategory(message, mediaFilter);

  if (normalizedFilter === "links") return <LinkIcon fontSize="small" />;
  if (mediaCategory === "image") return <PhotoCameraIcon fontSize="small" />;
  if (mediaCategory === "video") return <VideocamIcon fontSize="small" />;
  return <DescriptionIcon fontSize="small" />;
};

const MediaSearchListItem = ({ message, mediaFilter, searchTerm }) => {
  const classes = useStyles();
  const history = useHistory();
  const [thumbIndex, setThumbIndex] = useState(0);

  const ticket = message?.ticket;
  const contact = ticket?.contact;

  const handleOpen = () => {
    if (!ticket?.id) {
      return;
    }

    const targetUrl = message?.id
      ? `/tickets/${ticket.id}?messageId=${message.id}`
      : `/tickets/${ticket.id}`;

    history.push(targetUrl, {
      ticket: { id: ticket.id, uuid: ticket.uuid, code: uuidv4() },
      jumpToMessageId: message?.id,
      jumpPageNumber: message?.ticketPageNumber || null,
      jumpSearchTerm: searchTerm || undefined,
      jumpOrigin: "ticket-search-media",
    });
  };

  const mediaUrl = message?.mediaUrl || "";
  const mediaCategory = resolveMediaCategory(message, mediaFilter);
  const absoluteMediaUrl = resolveAbsoluteMediaUrl(mediaUrl);
  const galleryThumbUrl =
    mediaCategory === "image" || mediaCategory === "video"
      ? resolveThumbUrl(mediaUrl, "galeria-thumbnails")
      : "";
  const documentThumbUrl =
    mediaCategory === "document" ? resolveThumbUrl(mediaUrl, "pdf-thumbnails") : "";
  const thumbCandidates = [
    ...(mediaCategory === "document" ? [documentThumbUrl] : []),
    ...(mediaCategory === "image" || mediaCategory === "video" ? [galleryThumbUrl] : []),
    ...(mediaCategory === "image" ? [absoluteMediaUrl] : []),
  ].filter(Boolean);
  const thumbSource = thumbCandidates[thumbIndex] || "";
  const hasThumbSource = Boolean(thumbSource);
  const secondary = resolveMediaLabel(message, mediaFilter);
  const fallbackDocumentLabel = (() => {
    const normalized = normalizeFileName("", absoluteMediaUrl || mediaUrl, "documento.pdf");
    const extension = getExtensionFromName(normalized);
    return (extension || "doc").toUpperCase();
  })();

  useEffect(() => {
    setThumbIndex(0);
  }, [mediaUrl, mediaCategory, mediaFilter]);

  const handleThumbError = () => {
    setThumbIndex((previousIndex) => previousIndex + 1);
  };

  return (
    <ListItem button onClick={handleOpen} className={classes.mediaItem}>
      <ListItemAvatar>
        <ContactAvatar
          src={contact?.profilePicUrl}
          alt={contact?.name || contact?.number || "Contato"}
        />
      </ListItemAvatar>
      <ListItemText
        primary={contact?.name || contact?.number || ""}
        secondary={secondary}
      />
      <div className={classes.mediaRight}>
        {hasThumbSource ? (
          <img
            className={classes.mediaThumb}
            src={thumbSource}
            alt={secondary || ""}
            onError={handleThumbError}
          />
        ) : (
          <div
            className={classes.mediaThumb}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 12, fontWeight: 700 }}
          >
            {mediaCategory === "document" ? fallbackDocumentLabel : resolveMediaIcon(message, mediaFilter)}
          </div>
        )}
      </div>
    </ListItem>
  );
};

const TicketsSearchResults = ({
  searchParam,
  selectedQueueIds,
  tags,
  users,
  showAll,
  mediaFilter,
  style,
}) => {
  const classes = useStyles();

  const normalizedQueueIds = useMemo(
    () => (Array.isArray(selectedQueueIds) ? selectedQueueIds : []),
    [selectedQueueIds]
  );
  const normalizedTags = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);
  const normalizedUsers = useMemo(() => (Array.isArray(users) ? users : []), [users]);

  const isMediaMode = Boolean(mediaFilter);

  const [pageNumber, setPageNumber] = useState(1);
  const [targetPage, setTargetPage] = useState(1);
  const [refreshToken, setRefreshToken] = useState(0);

  const [contactResults, setContactResults] = useState([]);
  const [messageMatchResults, setMessageMatchResults] = useState([]);
  const [mediaResults, setMediaResults] = useState([]);

  const normalizedSearch = normalizeSearchTerm(searchParam);
  const hasSearchTerm = normalizedSearch.length > 0;

  const contactsQuery = useTickets({
    pageNumber,
    searchParam,
    searchScope: "contacts",
    showAll,
    tags: JSON.stringify(normalizedTags),
    users: JSON.stringify(normalizedUsers),
    queueIds: JSON.stringify(normalizedQueueIds),
    refreshToken,
    enabled: !isMediaMode && hasSearchTerm,
  });

  const messagesTicketsQuery = useTickets({
    pageNumber,
    searchParam,
    searchScope: "messages",
    showAll,
    tags: JSON.stringify(normalizedTags),
    users: JSON.stringify(normalizedUsers),
    queueIds: JSON.stringify(normalizedQueueIds),
    refreshToken,
    enabled: !isMediaMode && hasSearchTerm,
  });

  const mediaQuery = useSearchMessages({
    pageNumber,
    searchParam,
    mediaFilter,
    tags: JSON.stringify(normalizedTags),
    users: JSON.stringify(normalizedUsers),
    queueIds: JSON.stringify(normalizedQueueIds),
    refreshToken,
    enabled: isMediaMode && hasSearchTerm,
  });

  const loading = isMediaMode
    ? mediaQuery.loading
    : contactsQuery.loading || messagesTicketsQuery.loading;
  const hasMore = isMediaMode
    ? mediaQuery.hasMore
    : contactsQuery.hasMore || messagesTicketsQuery.hasMore;

  const filtersSnapshotRef = useRef({
    searchParam,
    showAll,
    queueIds: normalizedQueueIds,
    tags: normalizedTags,
    users: normalizedUsers,
    mediaFilter,
  });
  const contactsCycleReadyRef = useRef(false);
  const messagesCycleReadyRef = useRef(false);
  const mediaCycleReadyRef = useRef(false);

  useEffect(() => {
    const prev = filtersSnapshotRef.current;

    const searchChanged = prev.searchParam !== searchParam;
    const queueChanged = JSON.stringify(prev.queueIds) !== JSON.stringify(normalizedQueueIds);
    const tagsChanged = JSON.stringify(prev.tags) !== JSON.stringify(normalizedTags);
    const usersChanged = JSON.stringify(prev.users) !== JSON.stringify(normalizedUsers);
    const mediaChanged = prev.mediaFilter !== mediaFilter;
    const showAllChanged = prev.showAll !== showAll;

    if (!searchChanged && !queueChanged && !tagsChanged && !usersChanged && !mediaChanged && !showAllChanged) {
      return;
    }

    filtersSnapshotRef.current = {
      searchParam,
      showAll,
      queueIds: normalizedQueueIds,
      tags: normalizedTags,
      users: normalizedUsers,
      mediaFilter,
    };

    setContactResults([]);
    setMessageMatchResults([]);
    setMediaResults([]);
    contactsCycleReadyRef.current = false;
    messagesCycleReadyRef.current = false;
    mediaCycleReadyRef.current = false;
    setPageNumber(1);
    setTargetPage(1);
    setRefreshToken((prevToken) => prevToken + 1);
  }, [searchParam, showAll, normalizedQueueIds, normalizedTags, normalizedUsers, mediaFilter]);

  useEffect(() => {
    if (!hasSearchTerm) {
      contactsCycleReadyRef.current = false;
      messagesCycleReadyRef.current = false;
      mediaCycleReadyRef.current = false;
      return;
    }

    if (!isMediaMode && contactsQuery.loading) {
      contactsCycleReadyRef.current = true;
    }

    if (!isMediaMode && messagesTicketsQuery.loading) {
      messagesCycleReadyRef.current = true;
    }

    if (isMediaMode && mediaQuery.loading) {
      mediaCycleReadyRef.current = true;
    }
  }, [
    hasSearchTerm,
    isMediaMode,
    contactsQuery.loading,
    messagesTicketsQuery.loading,
    mediaQuery.loading,
  ]);

  useEffect(() => {
    if (isMediaMode || !hasSearchTerm) {
      return;
    }

    if (contactsQuery.loading) {
      return;
    }

    if (!contactsCycleReadyRef.current) {
      return;
    }

    const pageTickets = contactsQuery.tickets || [];
    if (!Array.isArray(pageTickets) || pageTickets.length === 0) {
      return;
    }

    setContactResults((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const next = [...prev];
      pageTickets.forEach((t) => {
        if (!existingIds.has(t.id)) {
          next.push(t);
        }
      });
      return next;
    });
  }, [isMediaMode, hasSearchTerm, contactsQuery.loading, contactsQuery.tickets]);

  useEffect(() => {
    if (isMediaMode || !hasSearchTerm) {
      return;
    }

    if (messagesTicketsQuery.loading) {
      return;
    }

    if (!messagesCycleReadyRef.current) {
      return;
    }

    const pageTickets = messagesTicketsQuery.tickets || [];
    if (!Array.isArray(pageTickets) || pageTickets.length === 0) {
      return;
    }

    setMessageMatchResults((prev) => {
      const existingIds = new Set(prev.map((match) => match.matchKey));
      const next = [...prev];
      pageTickets.forEach((ticket) => {
        const messages = Array.isArray(ticket?.messages) ? ticket.messages : [];

        messages.forEach((message) => {
          if (!message?.id) {
            return;
          }

          const matchKey = `${ticket.id}-${message.id}`;
          if (existingIds.has(matchKey)) {
            return;
          }

          const rawBody = message?.body || "";
          const bodyWithoutSignature = rawBody.replace(MESSAGE_SIGNATURE_PREFIX_REGEX, "");
          const snippet = buildMatchSnippet(bodyWithoutSignature, normalizedSearch);

          next.push({
            matchKey,
            ticket,
            messageId: message.id,
            messageCreatedAt: message.createdAt || null,
            messageUpdatedAt: message.updatedAt || null,
            displayAt: message.updatedAt || message.createdAt || null,
            snippet,
            term: normalizedSearch,
          });
          existingIds.add(matchKey);
        });
      });
      return next.sort((a, b) => {
        const aTime = a?.displayAt ? new Date(a.displayAt).getTime() : 0;
        const bTime = b?.displayAt ? new Date(b.displayAt).getTime() : 0;
        return bTime - aTime;
      });
    });
  }, [isMediaMode, hasSearchTerm, messagesTicketsQuery.loading, messagesTicketsQuery.tickets, normalizedSearch]);

  useEffect(() => {
    if (!isMediaMode || !hasSearchTerm) {
      return;
    }

    if (mediaQuery.loading) {
      return;
    }

    if (!mediaCycleReadyRef.current) {
      return;
    }

    const pageMessages = mediaQuery.messages || [];
    if (!Array.isArray(pageMessages) || pageMessages.length === 0) {
      return;
    }

    setMediaResults((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const next = [...prev];
      pageMessages.forEach((m) => {
        if (!existingIds.has(m.id)) {
          next.push(m);
        }
      });
      return next;
    });
  }, [isMediaMode, hasSearchTerm, mediaQuery.loading, mediaQuery.messages]);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (pageNumber >= targetPage) {
      return;
    }

    if (!hasMore) {
      setTargetPage((prev) => (prev > pageNumber ? pageNumber : prev));
      return;
    }

    setPageNumber((prev) => prev + 1);
  }, [loading, pageNumber, targetPage, hasMore]);

  const loadMore = () => {
    setTargetPage((prev) => prev + 1);
  };

  const documentListRef = useDocumentScrollPagination({
    enabled: hasMore && !loading && targetPage === pageNumber,
    loading,
    onLoadMore: loadMore,
  });

  const handleScroll = useScrollPreload({
    enabled: hasMore && !loading && targetPage === pageNumber,
    loading,
    onLoadMore: loadMore,
  });

  const segmented = useMemo(() => {
    if (isMediaMode) {
      const groups = groupMessagesByMonth(mediaResults);
      return { groups };
    }

    return {
      ticketsMatches: contactResults,
      messagesMatches: messageMatchResults,
    };
  }, [isMediaMode, mediaResults, contactResults, messageMatchResults]);

  const isEmpty = isMediaMode
    ? (segmented.groups || []).length === 0
    : (segmented.ticketsMatches || []).length === 0 && (segmented.messagesMatches || []).length === 0;

  return (
    <Paper square elevation={0} className={classes.wrapper} style={style}>
      <Paper
        square
        elevation={0}
        ref={documentListRef}
        data-mobile-document-list
        className={classes.listContainer}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {isEmpty && !loading ? (
            <div className={classes.empty}>
              <span style={{ fontWeight: 600 }}>{i18n.t("ticketsList.noTicketsTitle")}</span>
              <p style={{ marginTop: 8 }}>{i18n.t("ticketsList.noTicketsMessage")}</p>
            </div>
          ) : isMediaMode ? (
            <>
              {(segmented.groups || []).map((group) => (
                <React.Fragment key={group.key}>
                  <Typography className={classes.monthHeader} variant="subtitle2">
                    {group.label}
                  </Typography>
                  {group.items.map((message) => (
                    <MediaSearchListItem
                      key={message.id}
                      message={message}
                      mediaFilter={mediaFilter}
                      searchTerm={normalizedSearch}
                    />
                  ))}
                </React.Fragment>
              ))}
            </>
          ) : (
            <>
              {(segmented.ticketsMatches || []).length > 0 && (
                <Typography className={classes.sectionHeader} variant="subtitle2">
                  {i18n.t("tickets.search.sections.tickets")}
                </Typography>
              )}
              {(segmented.ticketsMatches || []).map((ticket) => (
                <TicketListItem
                  key={`tickets-${ticket.id}`}
                  ticket={ticket}
                />
              ))}

              {(segmented.messagesMatches || []).length > 0 && (
                <Typography className={classes.sectionHeader} variant="subtitle2">
                  {i18n.t("tickets.search.sections.messages")}
                </Typography>
              )}
              {(segmented.messagesMatches || []).map((match) => {
                return (
                  <TicketListItem
                    key={`messages-${match.matchKey}`}
                    ticket={match.ticket}
                    searchMatch={{
                      term: match.term,
                      snippet: match.snippet,
                      messageId: match.messageId,
                      messagePageNumber: match.messagePageNumber || null,
                      messageCreatedAt: match.messageCreatedAt,
                      messageUpdatedAt: match.messageUpdatedAt,
                      displayAt: match.displayAt,
                      matchKey: match.matchKey,
                    }}
                  />
                );
              })}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsSearchResults;
